import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  StateToken,
  Store,
} from '@ngxs/store';
import { insertItem, patch, updateItem } from '@ngxs/store/operators';
import { map } from 'rxjs';
import { tap } from 'rxjs/operators';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { DueListState } from 'src/app/due-list/state/due-list.state';
import { ClearLastDueListItem } from 'src/app/due-list/state/due-list.actions';
import { DueListRoutes, UserDrillsRoutes } from '../../core/enums/routes.enum';
import { DrillLog } from '../models/drills-log.model';
import {
  IApiResponseGroupDrills,
  IDrill,
  IDrillListItem,
  IDrillsGroupData,
  IDrillPerformResponse,
} from '../models/drills.model';
import { DrillsApiService } from '../services/drills-api.service';
import {
  CreateDrill,
  DeleteDrill,
  LoadDrillById,
  LoadDrillLog,
  LoadDrillDraftPerform,
  LoadDrills,
  LoadDrillPerform,
  PerformDrill,
  PostponeDrill,
  SaveDrillDraftPerform,
  UpdateDrill,
  ClearDrillsByMenu,
  LoadDrillsForIO,
  DeactivateDrill,
  ReactivateDrill,
  ClearCurrentDrill,
} from './drills.actions';

export interface DrillsStateModel {
  drills: IDrill[];
  drillsForIO: IdName[];
  drillsOverview: IDrillsGroupData[];
  drillsOverviewTotalPages: number;
  drillsList: IDrillListItem[];
  drillLog: DrillLog;
  drillVersions: IDrillPerformResponse[];
  draftPerform: IDrillPerformResponse | undefined;
  currentDrill: IDrill | undefined;
}
const DRILLS_STATE_TOKEN = new StateToken<DrillsStateModel>('drills');
@State({
  name: DRILLS_STATE_TOKEN,
  defaults: {
    drills: [],
    drillsForIO: [],
    drillsOverview: [],
    drillsOverviewTotalPages: 0,
    drillsList: [],
    drillLog: { records: [] },
    drillVersions: [],
    draftPerform: undefined,
    currentDrill: undefined,
  },
})
@Injectable()
export class DrillsState {
  constructor(
    private service: DrillsApiService,
    private store: Store
  ) {}

  @Action(LoadDrills, { cancelUncompleted: true })
  loadDrills({ getState, setState }: StateContext<DrillsStateModel>, { filter }: LoadDrills) {
    return this.service.loadDrillsForGrouping(filter).pipe(
      tap((response: IApiResponseGroupDrills) => {
        const state = getState();
        setState({
          ...state,
          drillsOverview: response.groupData,
          drillsOverviewTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadDrillById, { cancelUncompleted: true })
  loadDrillById(
    { getState, setState }: StateContext<DrillsStateModel>,
    { drillId }: LoadDrillById
  ) {
    const { drills } = getState();
    const drillExists = drills.some((drill) => drill.id === drillId);
    return this.service.loadDrillById(drillId).pipe(
      tap((drill) => {
        setState(
          patch({
            drills: drillExists
              ? updateItem<IDrill>((drillToUpdate) => drillToUpdate?.id === drillId, drill)
              : insertItem<IDrill>(drill),
            currentDrill: drill,
          })
        );
      })
    );
  }

  @Action(LoadDrillPerform)
  loadDrillVersionById(
    { getState, setState }: StateContext<DrillsStateModel>,
    { drillId, drillVersionId }: LoadDrillPerform
  ) {
    const { drillVersions } = getState();
    const drillExists = drillVersions.some((drillVersion) => drillVersion.id === drillVersionId);
    return this.service.loadDrillVersionById(drillId, drillVersionId).pipe(
      map((drillVersion) => {
        return { ...drillVersion, id: drillVersionId };
      }),
      tap((drillVersion) => {
        setState(
          patch({
            drillVersions: drillExists
              ? updateItem<IDrillPerformResponse>(
                  (drillVersionToUpdate) => drillVersionToUpdate?.id === drillVersionId,
                  drillVersion
                )
              : insertItem<IDrillPerformResponse>(drillVersion),
          })
        );
      })
    );
  }

  @Action(LoadDrillLog, { cancelUncompleted: true })
  loadDrillLogs({ getState, setState }: StateContext<DrillsStateModel>, { drillId }: LoadDrillLog) {
    return this.service.loadDrillLog(drillId).pipe(
      tap((drillLog) => {
        const state = getState();
        setState({ ...state, drillLog });
      })
    );
  }

  @Action(LoadDrillsForIO, { cancelUncompleted: true })
  loadDrillsForIO({ getState, setState }: StateContext<DrillsStateModel>) {
    return this.service.getDrillsForIO().pipe(
      tap((drillsForIO) => {
        const state = getState();
        setState({ ...state, drillsForIO });
      })
    );
  }

  @Action(CreateDrill, { cancelUncompleted: true })
  @OnSuccess({ message: 'drillCreated' })
  createDrill(ctx: StateContext<DrillsStateModel>, { drill }: CreateDrill) {
    return this.service.createDrill(drill).pipe(
      tap(() => {
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? drill.menuId;
        redirectAndBypassUnsavedDataCheck(`${UserDrillsRoutes.Overview}/${menuId}`);
      })
    );
  }

  @Action(UpdateDrill, { cancelUncompleted: true })
  @OnSuccess({ message: 'drillUpdated' })
  updateDrill(ctx: StateContext<DrillsStateModel>, { drill }: UpdateDrill) {
    return this.service.updateDrill(drill).pipe(
      tap(() => {
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? drill.menuId;
        redirectAndBypassUnsavedDataCheck(`${UserDrillsRoutes.Overview}/${menuId}`);
      })
    );
  }

  @Action(DeleteDrill, { cancelUncompleted: true })
  @OnSuccess({ message: 'drillDeleted', dispatch: ResetCurrentFilter })
  deleteDrill(ctx: StateContext<DrillsStateModel>, { drillId }: DeleteDrill) {
    return this.service.deleteDrill(drillId).pipe(
      tap(() => {
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);
        let url = `${UserDrillsRoutes.Main}`;
        if (menuId) {
          url = `${UserDrillsRoutes.Overview}/${menuId}`;
        }
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(PostponeDrill, { cancelUncompleted: true })
  @OnSuccess({ message: 'drillPostponed' })
  postponeDrill(stateCtx: StateContext<DrillsStateModel>, { drillPostpone }: PostponeDrill) {
    return this.service.postponeDrill(drillPostpone);
  }

  @Action(DeactivateDrill, { cancelUncompleted: true })
  @OnSuccess({ message: 'drillDeactivated' })
  deactivateDrill(stateCtx: StateContext<DrillsStateModel>, { id }: DeactivateDrill) {
    return this.service.deactivateDrill(id).pipe(
      tap(() => {
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);
        let url = `${UserDrillsRoutes.Main}`;
        if (menuId) {
          url = `${UserDrillsRoutes.Overview}/${menuId}`;
        }
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(ReactivateDrill, { cancelUncompleted: true })
  @OnSuccess({ message: 'drillReactivated' })
  reactivateDrill(stateCtx: StateContext<DrillsStateModel>, { id }: ReactivateDrill) {
    return this.service.reactivateDrill(id).pipe(
      tap(() => {
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);
        let url = `${UserDrillsRoutes.Main}`;
        if (menuId) {
          url = `${UserDrillsRoutes.Overview}/${menuId}`;
        }
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(PerformDrill, { cancelUncompleted: true })
  @OnSuccess({ message: 'drillPerformed' })
  performDrill(stateCtx: StateContext<DrillsStateModel>, { drill }: PerformDrill) {
    return this.service.performDrill(drill).pipe(
      tap(() => {
        const isFromDueList =
          this.store.selectSnapshot(DueListState.getLastDueListItem)?.id === drill.id;
        if (!isFromDueList) {
          redirectAndBypassUnsavedDataCheck(`${UserDrillsRoutes.Edit}/${drill.id}`);
        } else {
          this.store.dispatch(new ClearLastDueListItem());
          redirectAndBypassUnsavedDataCheck(DueListRoutes.Main);
        }
      })
    );
  }

  @Action(LoadDrillDraftPerform, { cancelUncompleted: true })
  loadDrillDraftPerform(
    { getState, setState }: StateContext<DrillsStateModel>,
    { drillId }: LoadDrillDraftPerform
  ) {
    return this.service.loadDrillDraftPerform(drillId).pipe(
      tap((draftPerform) => {
        setState({ ...getState(), draftPerform });
      })
    );
  }

  @Action(SaveDrillDraftPerform, { cancelUncompleted: true })
  @OnSuccess({ message: 'draftSaved' })
  saveDrillDraftPerform(
    { getState, setState }: StateContext<DrillsStateModel>,
    { Drill }: SaveDrillDraftPerform
  ) {
    return this.service.saveDrillDraftPerform(Drill).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserDrillsRoutes.Edit}/${Drill.id}`);
      })
    );
  }

  @Action(ClearDrillsByMenu, { cancelUncompleted: true })
  clearDrillsByMenu(ctx: StateContext<DrillsStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      drillsOverview: [],
      drillsOverviewTotalPages: 0,
    });
  }

  @Action(ClearCurrentDrill)
  clearCurrentDrill(ctx: StateContext<DrillsStateModel>) {
    ctx.setState(patch({ currentDrill: undefined }));
  }

  static getDrillById(id: string): (state: DrillsStateModel) => IDrill | undefined {
    return createSelector([DrillsState], (state: DrillsStateModel) => {
      return state.drills.find((drill) => drill.id === id);
    });
  }

  static getDrillPerformDataById(
    id: string
  ): (state: DrillsStateModel) => IDrillPerformResponse | undefined {
    return createSelector([DrillsState], (state: DrillsStateModel) => {
      return state.drillVersions.find((drillVersion) => drillVersion.id === id);
    });
  }

  @Selector()
  static getDrillsOverview(state: DrillsStateModel): IDrillsGroupData[] {
    return state.drillsOverview;
  }

  @Selector()
  static getDrillsOverviewTotalPages(state: DrillsStateModel): number {
    return state.drillsOverviewTotalPages;
  }

  @Selector()
  static getDrillLog(state: DrillsStateModel) {
    return state.drillLog;
  }

  @Selector()
  static getDraftPerform(state: DrillsStateModel) {
    return state.draftPerform;
  }

  @Selector()
  static getDrillsForIO(state: DrillsStateModel) {
    return state.drillsForIO;
  }

  @Selector()
  static getCurrentDrill(state: DrillsStateModel): IDrill | undefined {
    return state.currentDrill;
  }
}
